// Weather particles, drawn procedurally. Respects the reduced-motion setting,
// which disables particles entirely (UI-DESIGN.md §6).

import { Container, Graphics } from 'pixi.js';
import type { WeatherCondition } from '../core/types';

interface Particle {
  x: number;
  y: number;
  speed: number;
  length: number;
  drift: number;
}

const DENSITY: Record<WeatherCondition, number> = {
  CLEAR: 0,
  CLOUDY: 0,
  FOG: 0,
  RAIN: 160,
  STORM: 420,
};

/**
 * Screen-space overlay: particles fall over the whole viewport rather than
 * being placed in the world, so panning never reveals an empty edge.
 */
export class WeatherFx extends Container {
  private graphics = new Graphics();
  private particles: Particle[] = [];
  private condition: WeatherCondition = 'CLEAR';
  private width0 = 0;
  private height0 = 0;
  private reducedMotion = false;
  private lightningTimer = 0;
  private lightningFlash = new Graphics();

  constructor() {
    super();
    this.addChild(this.graphics, this.lightningFlash);
    this.lightningFlash.visible = false;
  }

  resize(width: number, height: number): void {
    this.width0 = width;
    this.height0 = height;
    this.rebuild();
  }

  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
    this.rebuild();
  }

  setCondition(condition: WeatherCondition): void {
    if (condition === this.condition) return;
    this.condition = condition;
    this.rebuild();
  }

  private rebuild(): void {
    const count = this.reducedMotion ? 0 : DENSITY[this.condition];
    this.particles = Array.from({ length: count }, () => this.spawn(true));
    if (count === 0) this.graphics.clear();
  }

  private spawn(anywhere: boolean): Particle {
    const stormy = this.condition === 'STORM';
    return {
      x: Math.random() * (this.width0 + 200) - 100,
      y: anywhere ? Math.random() * this.height0 : -20,
      speed: (stormy ? 1400 : 900) + Math.random() * 400,
      length: stormy ? 22 : 14,
      // Storms blow rain sideways; light rain falls nearly straight.
      drift: stormy ? 380 : 90,
    };
  }

  /** `deltaMs` is real time — weather looks wrong if it stutters with the sim. */
  update(deltaMs: number): void {
    if (this.particles.length === 0) {
      this.updateLightning(deltaMs);
      return;
    }
    const dt = deltaMs / 1000;
    const g = this.graphics;
    g.clear();
    g.lineStyle({ width: this.condition === 'STORM' ? 2 : 1, color: 0xcfe3f5, alpha: 0.55 });

    for (const particle of this.particles) {
      particle.y += particle.speed * dt;
      particle.x += particle.drift * dt;
      if (particle.y > this.height0 + 20 || particle.x > this.width0 + 120) {
        Object.assign(particle, this.spawn(false));
      }
      g.moveTo(particle.x, particle.y);
      g.lineTo(particle.x - particle.drift * 0.012, particle.y + particle.length);
    }
    g.lineStyle(0);
    this.updateLightning(deltaMs);
  }

  /** Occasional lightning during storms — skipped under reduced motion. */
  private updateLightning(deltaMs: number): void {
    if (this.condition !== 'STORM' || this.reducedMotion) {
      this.lightningFlash.visible = false;
      return;
    }
    this.lightningTimer -= deltaMs;
    if (this.lightningTimer <= 0) {
      this.lightningTimer = 3000 + Math.random() * 7000;
      this.lightningFlash.clear();
      this.lightningFlash.beginFill(0xffffff, 0.35);
      this.lightningFlash.drawRect(0, 0, this.width0, this.height0);
      this.lightningFlash.endFill();
      this.lightningFlash.visible = true;
      this.lightningFlash.alpha = 1;
    } else if (this.lightningFlash.visible) {
      this.lightningFlash.alpha -= deltaMs / 120;
      if (this.lightningFlash.alpha <= 0) this.lightningFlash.visible = false;
    }
  }
}
